import { BaseUIService } from '@/modules/core/ui/services/BaseUIService';
import { Modal } from '@/modules/core/ui/services/Modal';
import type { App, Component } from 'vue';

// Type definition for Buefy Vue 3
export interface BModalComponent {
  close: () => void;
  $refs: Record<string, unknown>;
  $el: unknown;
  $parent?: unknown;
  [key: string]: unknown;
}

export type ModalOptions = {
  active?: boolean;
  component: Component;
  parent?: Component; // Optional in Vue 3 - app instance handles context automatically
  props?: Record<string, unknown>;
  events?: object;
  content?: string;
  width?: number | string;
  hasModalCard?: boolean;
  animation?: string;
  canCancel?: boolean | ('escape' | 'x' | 'outside' | 'button')[];
  onCancel?: () => void;
  scroll?: string;
  customClass?: string;
  fullScreen?: boolean;
  modalId?: string;
  trapFocus?: boolean;
};

export class ModalService extends BaseUIService {
  public modals: Modal[] = [];

  setApp(app: App): void {
    Modal.setApp(app);
  }

  get isShowingModal(): boolean {
    return !!this.modals.length && !!document.querySelector('div.modal.is-active');
  }

  get currentModal(): Modal | null {
    return this.isShowingModal ? this.modals[this.modals.length - 1] : null;
  }

  get currentModalBody(): Element | null {
    if (!this.isShowingModal) {
      return null;
    }
    const modal: Modal = this.modals[this.modals.length - 1];
    if (!modal) {
      return null;
    }

    return modal.component.$el?.querySelector?.('section.modal-card-body');
  }

  show(options: ModalOptions): BModalComponent {
    if (options.parent) {
      delete options.parent;
    }

    // @ts-ignore
    const modal: Modal = new Modal(options);
    modal.show((modal: Modal) => this.onClose(modal));
    this.modals.push(modal);

    return modal.component;
  }

  close(): void {
    if (this.isShowingModal) {
      this.currentModal?.close();
    }
  }

  closeToIndex(value: number): void {
    if (!this.isShowingModal) {
      return;
    }
    this.modals.slice(-value).forEach((modal: Modal) => modal.close());
  }

  closeAll(): void {
    if (this.isShowingModal) {
      this.modals.slice().forEach((modal: Modal) => modal.close());
    }
  }

  closeWithId(modalId: string): void {
    const modal: Modal | undefined = this.modals.find((modal: Modal) => modal.isId(modalId));
    modal?.close();
  }

  isShowingModalWithId(modalId: string): boolean {
    return this.isShowingModal && this.modals.some((modal: Modal) => modal.isId(modalId));
  }

  closeWithComponent(component: unknown): void {
    const modal: Modal | null = this.getModalFromComponent(component);
    modal?.close();
  }

  private getModalFromComponent(component: unknown): Modal | null {
    return (
      this.modals.find((modal: Modal) => modal.component === component) ??
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-explicit-any
      ((component as any)?.$parent ? this.getModalFromComponent((component as any).$parent) : null)
    );
  }

  private onClose(modal: Modal): void {
    this.modals = this.modals.filter((modalInArray: Modal) => modalInArray !== modal);
  }
}

export default new ModalService();
